import React, { useEffect, useMemo, useState } from 'react'
import { getWaiter } from 'redux-waiter'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { MrModal, clearModal } from '@hixme/modal'
import dayjs from 'dayjs'

// hixme-ui
import Button from '@hixme-ui/button'
import ContentContainer from '@hixme-ui/content-container'
import Container from '@hixme-ui/container'
import Title from '@hixme-ui/title'

import { Spin, Table, Tag } from '@surecompanies/core_components'
import { TIMEZONE_PST } from 'helpers/datetime'
import { CurrencyFormatter } from 'helpers/formatters'
import notification from 'modules/app-core/notification-manager'
import { getVirtualCardByBenefitId } from 'modules/virtual-cards/selectors'
import { VirtualCardType } from 'modules/virtual-cards/constants'
import { normalizeVcnProvider } from 'modules/virtual-cards/normalizeVcnProvider'

import { GET_TRANSACTIONS } from 'routes/Employees/Employee/EmployeeCoverage/modules/employee-benefits/constants'
import { VCNService } from 'routes/Employees/Employee/EmployeeCoverage/modules/employee-benefits/api'
import { TimezoneSelect } from 'enrollment-platform/pages/finance/components/PaymentAssignment/Utils/TimezoneSelect'

const currencyFormatter = new CurrencyFormatter()

const STATUS_COLOR = {
  Approved: 'default',
  Authorized: 'blue',
  Declined: 'red',
  Pending: 'orange',
  Settled: 'green',
  Failed: 'red',
  Voided: 'red',
  AUTHORIZED: 'blue',
  PENDING: 'orange',
  SETTLED: 'green',
  DECLINED: 'red',
}

const SOURCE_COLOR = {
  ACH: 'purple',
  MARQETA: 'cyan',
  VCN: 'cyan',
  WEX: 'gold',
}

const TransactionModal = ({
  onClose,
  onError,
  isLoading,
  getCardTransactions,
}) => {
  const [tz, setTz] = useState('local')
  const [transactions, setTransactions] = useState([])

  const formatDate = (value) => {
    if (!value) return '—'
    const resolvedTz =
      tz === 'local'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : TIMEZONE_PST
    return dayjs(value).tz(resolvedTz).format('YYYY-MM-DD hh:mm A')
  }

  const columns = useMemo(
    () => [
      {
        title: 'Date',
        dataIndex: 'transaction_date',
        key: 'transaction_date',
        render: (value) => formatDate(value),
        sorter: (a, b) =>
          Date.parse(a.transaction_date) - Date.parse(b.transaction_date),
        defaultSortOrder: 'descend',
      },
      {
        title: 'Amount',
        dataIndex: 'transaction_amount',
        key: 'transaction_amount',
        align: 'right',
        render: (value) =>
          value != null ? currencyFormatter.format(Number(value)) : '—',
        sorter: (a, b) => a.transaction_amount - b.transaction_amount,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        render: (value) => (
          <Tag color={STATUS_COLOR[value] ?? 'default'}>{value || '—'}</Tag>
        ),
        sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        align: 'center',
        render: (value) => value || '—',
        sorter: (a, b) => (a.type || '').localeCompare(b.type || ''),
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        align: 'center',
        render: (value) => (
          <Tag color={SOURCE_COLOR[value] ?? 'default'}>{value || '—'}</Tag>
        ),
        sorter: (a, b) => (a.source || '').localeCompare(b.source || ''),
      },
      {
        title: 'Card',
        dataIndex: 'vcn_card',
        key: 'vcn_card',
        align: 'center',
        render: (value) => value || '—',
        sorter: (a, b) => (a.vcn_card || '').localeCompare(b.vcn_card || ''),
      },
    ],
    [tz]
  )

  useEffect(() => {
    let isMounted = true

    getCardTransactions()
      .then((res) => {
        if (isMounted) {
          setTransactions(res.transactions ?? [])
        }
      })
      .catch(onError)
    return () => {
      isMounted = false
    }
  }, [])

  return (
    <MrModal>
      <ContentContainer contentMargin="20px auto 0" contentWidth="1024px">
        <Title margin="20px 0">Transaction History</Title>
        <Container
          flex
          direction="column"
          style={{ maxHeight: '600px', overflowX: 'auto' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12,
            }}
          >
            <span style={{ fontSize: 12, color: '#677786' }}>Timezone:</span>
            <TimezoneSelect value={tz} onChange={setTz} />
          </div>
          {isLoading ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Table
              dataSource={transactions}
              columns={columns}
              rowKey="payment_transaction_id"
              pagination={false}
              locale={{ emptyText: 'No transactions found' }}
              size="small"
              scroll={{ x: true }}
            />
          )}
        </Container>
        <Container>
          <Button primary onClick={onClose}>
            Close
          </Button>
        </Container>
      </ContentContainer>
    </MrModal>
  )
}

const mapStateToProps = (state, ownProps) => {
  const benefitId = ownProps.id ?? ownProps.data?.id
  const virtualCard = benefitId
    ? getVirtualCardByBenefitId(state)(benefitId)
    : undefined
  const merchantLogId =
    normalizeVcnProvider(virtualCard?.vcnProvider) === VirtualCardType.WEX &&
    virtualCard?.token
      ? virtualCard.token
      : undefined

  return {
    benefitId,
    merchantLogId,
    isLoading: getWaiter(state, GET_TRANSACTIONS).isPending,
  }
}

const mapDispatchToProps = (dispatch) => ({
  onClose: () => dispatch(clearModal()),
  fetchCombinedTransactions: (benefitId, merchantLogId) =>
    dispatch(VCNService.getTransactions(benefitId, { merchantLogId })),
  onError: () =>
    dispatch(
      notification.actions.createErrorNotification(
        'Something went wrong getting your transactions information.'
      )
    ),
})

const mergeProps = (stateProps, dispatchProps) => {
  const { fetchCombinedTransactions, ...dispatchRest } = dispatchProps
  return {
    isLoading: stateProps.isLoading,
    onClose: dispatchRest.onClose,
    onError: dispatchRest.onError,
    getCardTransactions: () =>
      fetchCombinedTransactions(stateProps.benefitId, stateProps.merchantLogId),
  }
}

TransactionModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  getCardTransactions: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(TransactionModal)
